import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Mail, Phone, Search, Users, Building2 } from "lucide-react";
import { fmtDateTime } from "@/lib/format";

// 34.docx — read-only directory of CRM contacts added/updated by salespersons
// under their clients/accounts. Auto-reflects every add/update because it
// reads straight from db.contacts on load.
export default function ContactsDirectory() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", { params: q ? { q } : {} });
        setRows(data);
      } catch {
        setRows([]);
      }
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q]);

  // Dedupe by email/phone (keep the most recently updated) + newest first.
  const list = useMemo(() => {
    if (!rows) return null;
    const ts = (r) => new Date(r.updated_at || r.created_at || 0).getTime();
    const sorted = [...rows].sort((a, b) => ts(b) - ts(a));
    const seen = new Set();
    const out = [];
    for (const r of sorted) {
      const key = (r.email && `e:${r.email.toLowerCase()}`) || (r.phone && `p:${r.phone}`) || `id:${r.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
    return out;
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="contacts-directory-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Customers</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Contacts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every contact your team has added under clients &amp; accounts — updates reflect here automatically.
          </p>
        </div>
        {list && (
          <Badge variant="outline" className="w-fit" data-testid="contacts-directory-count">
            <Users className="h-3.5 w-3.5 mr-1" /> {list.length} contact{list.length === 1 ? "" : "s"}
          </Badge>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="contacts-directory-search"
          className="pl-9"
          placeholder="Search name, company, phone or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {!list ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}
        </div>
      ) : list.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="contacts-directory-empty">
          No contacts yet. Contacts added by salespersons under clients/accounts appear here automatically.
        </CardContent></Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden md:block overflow-hidden">
            <table className="w-full text-sm" data-testid="contacts-directory-table">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-bold">Contact Name</th>
                  <th className="px-4 py-3 font-bold">Company / Client</th>
                  <th className="px-4 py-3 font-bold">Phone</th>
                  <th className="px-4 py-3 font-bold">Email</th>
                  <th className="px-4 py-3 font-bold">Owner / Salesperson</th>
                  <th className="px-4 py-3 font-bold">Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors" data-testid={`contacts-directory-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold">
                      {r.name || "—"}
                      {r.designation && <div className="text-xs text-muted-foreground font-normal">{r.designation}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {r.client_id ? (
                        <Link to={`/clients/${r.client_id}`} className="text-primary hover:underline font-medium">
                          {r.company_name || "View client"}
                        </Link>
                      ) : (r.company_name || "—")}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.phone || "—"}</td>
                    <td className="px-4 py-3">{r.email || "—"}</td>
                    <td className="px-4 py-3">{r.owner_name || r.created_by_name || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDateTime(r.updated_at || r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {list.map((r) => (
              <Card key={r.id} data-testid={`contacts-directory-card-${r.id}`}>
                <CardContent className="p-4 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{r.name || "—"}</p>
                      {r.designation && <p className="text-xs text-muted-foreground">{r.designation}</p>}
                    </div>
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtDateTime(r.updated_at || r.created_at)}</span>
                  </div>
                  <p className="text-xs flex items-center gap-1.5 text-muted-foreground">
                    <Building2 className="h-3.5 w-3.5" />
                    {r.client_id ? (
                      <Link to={`/clients/${r.client_id}`} className="text-primary hover:underline">{r.company_name || "View client"}</Link>
                    ) : (r.company_name || "—")}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {r.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{r.phone}</span>}
                    {r.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{r.email}</span>}
                  </div>
                  <p className="text-[11px] text-muted-foreground">Owner: <span className="font-medium text-foreground">{r.owner_name || r.created_by_name || "—"}</span></p>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
